import { push } from './_shared';
import { NAME_PUSH_EVENTS, MODULE_ACTIONS } from '../constants';
import { AnalyticsError } from '../errors';
import { logger } from '../services';
/**
 * Builds out event data for a generic page interaction
 *
 * @param {Object} params
 *    To build out event data for generic page interactions
 * @param {string} params.name
 *    A standardized component name (aka, "module name") from the "Component Inventory" Confluence page. These values
 *    should match accepted values, and are NOT arbitrary. However, for sanity's and maintenance sake, this value
 *    isn't enforced/validated by this library, as there are multiple dozens of components.
 *
 *    For reference: https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 * @param {string} params.action
 *    The action the component is taking. Note that similar to component values, these also have standardized names,
 *    and should not be arbitrary strings. You can (should) use the CONSTANTS property of `MODULE_ACTIONS`
 *
 *    For reference: https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 * @param {string} params.value
 *    Mostly an arbitrary value. For example, carousel/sliders would have the title of the slide. Note! This value is
 *    auto-truncated to be 255 max characters before sending
 *
 * @return void
 *
 * @see MODULE_ACTIONS
 * @see https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 */

var pushEventModule = function pushEventModule() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    action = _ref.action,
    name = _ref.name,
    value = _ref.value;

  // Make sure nothing is undefined. Unfortunately, JS doesn't do this out
  // of the box for us when we use param destructuring
  if (!action || !name || !value) {
    throw new TypeError("The 'action|name|value' parameters must all be defined");
  }
  if (!Object.values(MODULE_ACTIONS).includes(action)) {
    var validActions = Object.values(MODULE_ACTIONS).join(', ');
    logger.error(new AnalyticsError("The action value of '".concat(action, "' is not valid. Check the \"Component Library Inventory\" Confluence page, or with someone at DART, to confirm that it's valid. Valid actions: ").concat(validActions)));
  } // So we don't accidentally send over a whole paragraph's worth of a String
  // if, for example, we end up interacting with a slider/carousel where
  // the value is some customer testimonial that is an essay

  value = String(value).substring(0, 255);
  push(NAME_PUSH_EVENTS.PAGE_INTERACTION, {
    interaction: {
      action: action,
      module: name,
      value: value
    }
  });
};
/**
 * Builds out event data for a generic page interaction
 *
 * This can also be used for interactions on common components within any of the other event types, such as a slider or
 * some table of contents on a quiz or FAFA.
 *
 * Both the `name` and `action` prop have validation and won't just take in any arbitrary string, to maintain data
 * consistency with the Adobe side. For both these parameters, see `CONSTANTS.NAME_PUSH_EVENTS` and
 * `CONSTANTS.MODULE_ACTIONS` for `name` and `action`, respectively
 *
 * Example:
 *    ```javascript
 *    // yourFile.js
 *    //
 *    import { CONSTANTS, events } from '@gelato/analytics-datalayer'
 *
 *    events.modules.interact({
 *      // From the Confluence page, and this would evaluate to
 *      // something like the (string), "module-scroll"
 *      action: CONSTANTS.MODULE_ACTIONS.MODULE_SCROLL, // "module-scroll"
 *
 *      // This is from the Confluence page
 *      name: 'video-module:b',
 *
 *      // The title of the video module, if applicable.
 *      // This is flexible, and may need some coordination
 *      value: <slide title viewed>,
 *    })
 *    ```
 *
 * NOTE! Despite the name of this function, the actual event name is called "page-interaction". The discrepancy is for
 * better human readability/understanding, because "pushEventPageInteraction" is such an incredibly generic and
 * vague name, and the Confluence page documenting these calls is called "Component {...}"
 *
 * @param {Object} params
 *    To build out event data for generic page interactions
 * @param {string} params.name
 *    A standardized component name (aka, "module name") from the "Component Inventory" Confluence page. These values
 *    should match accepted values, and are NOT arbitrary. However, for sanity's and maintenance sake, this value
 *    isn't enforced/validated by this library, as there are multiple dozens of components.
 *
 *    For reference: https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 * @param {string} params.action
 *    The action the component is taking. Note that similar to component values, these also have standardized names,
 *    and should not be arbitrary strings. You can (should) use the CONSTANTS property of `MODULE_ACTIONS`
 *
 *    For reference: https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 * @param {string} params.value
 *    Mostly an arbitrary value. For example, carousel/sliders would have the title of the slide. Note! This value is
 *    auto-truncated to be 255 max characters before sending
 *
 * @return void
 *
 * @see MODULE_ACTIONS
 * @see https://confluence.nml.com/display/CPGT/Component+Library+Inventory
 */

var interact = function interact(_ref2) {
  var action = _ref2.action,
    name = _ref2.name,
    value = _ref2.value;
  return pushEventModule({
    action: action,
    name: name,
    value: value
  });
};
export { interact };